import { assert_type_enum } from '@shared/asserts/utils_asserts';
import { CONSTANTS_GENERATION__slider_step } from '@shared/constants/CONSTANTS_GENERATION__slider_step';
import { CONSTANTS_GENERATION__value_default } from '@shared/constants/CONSTANTS_GENERATION__value_default';
import { CONSTANTS_GENERATION__value_range as CONSTANTS_RANGE } from '@shared/constants/CONSTANTS_GENERATION__value_range';
import { DTO__word } from '@shared/dto/+word/word_translated/DTO__word';
import { ENUM__language_type } from '@shared/enums/ENUM__language_type';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { DTO_GRAPH__instance } from '../../_graph/DTO_GRAPH__instance';
import { ENUM__field_connectable_type } from '../_base/_methods/ENUM__field_connectable_type';
import { FIELD__base } from '../_base/FIELD__base';
import { DECORATOR__class_serialization_tested } from '../_primitives/_base/DECORATOR__class_serialization_tested';
import { DECORATOR__class_serialization_tested_for_hash } from '../_primitives/_base/DECORATOR__class_serialization_tested_for_hash';
import { DECORATOR__field_is_input } from '../_primitives/_base/DECORATOR__field_is_input';
import { DECORATOR__field_not_linked } from '../_primitives/_base/DECORATOR__field_not_linked';
import { FIELD_PRIMITIVE__bool } from '../_primitives/FIELD_PRIMITIVE__bool';
import type { FIELD__list_token } from '../_primitives/list/FIELD__list_token';
import { FIELD_PRIMITIVE__float } from '../_primitives/number/FIELD_PRIMITIVE__float';
import { FIELD_PRIMITIVE__base_string } from '../_primitives/string/FIELD_PRIMITIVE__base_string';
import { ENUM__token_type } from './ENUM__token_type';

@DECORATOR__class_serialization_tested
@DECORATOR__class_serialization_tested_for_hash
export class FIELD__token extends FIELD__base {
  /*
   * STATIC FIELDS
   */

  static _class_name_static = ENUM__dto_class_name.GRAPH_FIELD_TOKEN;

  /*
   *
   */
  static sort(a: FIELD__token, b: FIELD__token) {
    let out;
    if (a.is_positive.get_value() != b.is_positive.get_value()) {
      out = a.is_positive.get_value() ? -1 : 1;
    } else {
      let word_a = a.word_english.get_value();
      let word_b = b.word_english.get_value();

      out = word_a.localeCompare(word_b);
    }

    return out;
  }

  /*
   * STATIC METHODS
   */

  static sort_by_word_english(a: FIELD__token, b: FIELD__token) {
    return a.word_english.get_value().localeCompare(b.word_english.get_value());
  }

  static from_string_get_words(prompt: string) {
    const out = prompt
      .split(/,\s*|\r?\n/)
      .map(v => v.replace(/[^\p{L}\p{N}\s.:()-]/gu, '').trim_lowercase(false))
      .filter(v => v.length > 0);

    return out;
  }

  static from_string_to_token_list(prompt: string, field_list: FIELD__list_token) {
    let out: FIELD__token[] = [];

    const values_string = FIELD__token.from_string_get_words(prompt);

    for (let value of values_string) {
      let token = field_list.create_item_new__default();

      FIELD__token.fill_field_from_token_string(value, token);

      out.push(token);
    }

    return out;
  }

  static fill_field_from_token_string(token_string: string, field: FIELD__token) {
    let weight: number = CONSTANTS_GENERATION__value_default.TOKEN_WEIGHT;

    const match = token_string.match(/\(([^)]+)\)/);
    if (match) token_string = match[1].trim();

    if (token_string.includes(':')) {
      const token_and_weight = token_string.split(':');
      const token_value = token_and_weight[0].trim_lowercase();

      const weight_string = token_and_weight[1].trim_lowercase();
      const weight_number = parseFloat(weight_string);
      if (!isNaN(weight_number)) weight = Math.round(weight_number * 10) / 10;

      token_string = token_value;
    }

    if (token_string.startsWith('-')) {
      token_string = token_string.slice(1);
      field.is_positive.set_value(false);
    } else {
      field.is_positive.set_value(true);
    }

    field.type = ENUM__token_type.WORD;
    field.word_english.set_value(token_string);

    weight = field.weight.get_values_allowed().clamp(weight);

    field.weight.value = weight;
  }

  /*
   * EVENTS
   */

  /*
   * METHODS
   */

  /*
   * FIELDS
   */
  type!: ENUM__token_type;

  @FIELD_PRIMITIVE__base_string.init_as_field({ value: '' }, { grid2d__can_be_dimension: false, ui__has_component: false })
  word_english!: FIELD_PRIMITIVE__base_string;

  @FIELD_PRIMITIVE__bool.init_as_field({ value: true }, { grid2d__can_be_dimension: false, ui__has_component: false })
  is_positive!: FIELD_PRIMITIVE__bool;

  @FIELD_PRIMITIVE__float.init_as_field(
    {
      value: 1.0,
      value_range: CONSTANTS_RANGE.WEIGHT_RANGE,
      values_allowed: CONSTANTS_RANGE.WEIGHT_ALLOWED,

      slider_step: CONSTANTS_GENERATION__slider_step.TOKEN_WEIGHT_DEFAULT,
      slider_step_values: CONSTANTS_GENERATION__slider_step.TOKEN_WEIGHT_VALUES,
      multiple_of: 0,
    },
    { ui__has_component: false },
  )
  weight!: FIELD_PRIMITIVE__float;

  @FIELD_PRIMITIVE__bool.init_as_field({ value: false }, { grid2d__can_be_dimension: false, ui__has_component: false })
  is_hidden!: FIELD_PRIMITIVE__bool;

  /*
   * META FIELDS
   */

  word!: DTO__word;

  /*
   * PROPERTIES
   */

  field_connectable__type(): ENUM__field_connectable_type {
    return ENUM__field_connectable_type.TOKEN;
  }

  field_connectable__value(graph: DTO_GRAPH__instance): string {
    return this.toString();
  }

  @DECORATOR__field_not_linked()
  has_effect_on_generation_runtime(graph: DTO_GRAPH__instance) {
    if (this.is_hidden.get_value()) return false;
    if (this.weight.get_value() == 0) return false;

    return super.has_effect_on_generation_runtime(graph);
  }

  /*
   * METHODS
   */

  constructor() {
    super();

    this.type = ENUM__token_type.WORD;
    this.word = DTO__word.CREATE_STUB();
  }

  get_word_string(language: string, use_english: boolean): string {
    let out = this.word_english.get_value();

    if (language == ENUM__language_type.ENGLISH) return out; // language in settings is english
    if (use_english) return out; // user uses english for tokens
    if (this.word.is_stub()) return out; // dont have word object
    if (!this.word.word_translated) return out; // word is not translated yet
    if (this.word.language != language) return out; // we dont have translation for this language here yet. this case should be never triggered by design

    out = this.word.word_translated;

    return out;
  }

  is_one_word(): boolean {
    return !this.word_english.get_value().trim().includes(' ');
  }

  toString() {
    let sign = this.is_positive.get_value() ? '' : '-';

    let out = `${sign}${this.word_english.get_value()}:${this.weight.get_value()}`;

    return out;
  }

  /*
   *
   */
  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  store__serialization_keys_list_black(): string[] {
    let out: (keyof this)[] = super.store__serialization_keys_list_black() as (keyof this)[];

    out.push('word');

    return out as string[];
  }

  @DECORATOR__field_is_input()
  @DECORATOR__field_not_linked()
  store__serialization_keys_list_white_for_hash(): string[] {
    let out: (keyof this)[] = [];

    out.push('type');
    out.push('word_english');
    out.push('weight');
    out.push('is_positive');

    return out as string[];
  }

  hash__create__case_input__only_core(graph: DTO_GRAPH__instance) {
    return super.hash__create__case_input__only_core(graph);
  }

  /*
   *
   */

  assert_standard_fields() {
    super.assert_standard_fields();

    assert_type_enum(this.type, ENUM__token_type);
  }
}
